import { chain, thirdWebClient } from "@/app/libs/thirdweb";
import { sponAirdropContract } from "@/contracts/addresses";
import AirdropPoolAbi from "@/contracts/abis/AirdropPool.json";
import SponAirdropAbi from "@/contracts/abis/SPONAirdrop.json";
import { ethers } from "ethers";
import {
  getContract,
  prepareContractCall,
  sendAndConfirmTransaction,
} from "thirdweb";
import type { Abi } from "thirdweb/utils";
import { Account } from "thirdweb/wallets";

export interface IClaimAirdropParams {
  shares: bigint;
  proof: `0x${string}`[];
  account: Account;
}

export const claimAirdrop = async ({
  shares,
  proof,
  account,
}: IClaimAirdropParams) => {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: sponAirdropContract,
      // See the note in `services/vesting.ts`: the ABI has to be passed in for
      // thirdweb to decode this contract's custom errors on a revert.
      abi: SponAirdropAbi as unknown as Abi,
    });

    const transaction = prepareContractCall({
      contract,
      method: "function claimAirdrop(uint256 amount, bytes32[] proof)",
      params: [shares, proof],
    });

    const transactionReceipt = await sendAndConfirmTransaction({
      transaction,
      account,
    });
    console.log("Transaction receipt:", transactionReceipt);
    return transactionReceipt;
  } catch (error) {
    console.error("Error claiming airdrop:", error);
    throw error;
  }
};

export const getAirdropDetails = async (address: string, signature: string) => {
  try {
    const response = await fetch("/api/get-airdrop-details", {
      method: "POST",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ address, signature }),
    });
    const responseJson = await response.json();
    return responseJson;
  } catch (error) {
    console.log("error in checking eligibility:", error);
    return error;
  }
};

export interface UserInfo {
  claimed: boolean;
  totalShares: string;
  claimedShares: string;
  lastClaimTime: string;
}

/**
 * Fetches claim information for a specific address from the contract using ethers.js
 * @param contractAddress The address of the airdrop contract
 * @param userAddress The address to check claim info for
 * @param provider An ethers provider
 * @returns Promise resolving to UserInfo object
 */
export const fetchClaimInfoEthers = async (
  contractAddress: string,
  userAddress: string,
  provider: ethers.Provider
): Promise<UserInfo> => {
  try {
    const contract = new ethers.Contract(
      contractAddress,
      AirdropPoolAbi,
      provider
    );

    const result = await contract.claimInfo(userAddress);

    const userInfo: UserInfo = {
      claimed: result[0],
      totalShares: result[1].toString(),
      claimedShares: result[2].toString(),
      lastClaimTime: result[3].toString(),
    };

    console.log(`Claim info for ${userAddress}:`, userInfo);
    return userInfo;
  } catch (error) {
    console.error("Error fetching claim info:", error);
    throw error;
  }
};

export const getClaimInfoEthers = async (
  userAddress: string,
  provider: ethers.Provider
) => {
  try {
    const contract = new ethers.Contract(
      sponAirdropContract,
      SponAirdropAbi,
      provider
    );

    const result = await contract.getClaimInfo(userAddress);

    const claimInfo = {
      claimed: result[0],
      claimedShares: result[1].toString(),
      claimTime: result[2].toString(),
    };

    console.log(`Claim info for ${userAddress}:`, claimInfo);
    return claimInfo;
  } catch (error) {
    console.error("Error fetching claim info:", error);
    throw error;
  }
};
